import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Theme, Flex, Box, Text, Button, TextField, Card } from '@radix-ui/themes';
import '@radix-ui/themes/styles.css';
import { isValidImei, IMEI_RESCAN_MESSAGE } from '../utils/imei';

type ScanState = 'loading' | 'ready' | 'capturing' | 'processing' | 'review' | 'submitting' | 'success' | 'error';

/** Compress and convert any image to JPEG, resized to maxSize px on longest side. */
const compressImage = (file: File, maxSize = 1200, quality = 0.8): Promise<Blob> => {
  return new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > height && width > maxSize) {
        height = (height * maxSize) / width;
        width = maxSize;
      } else if (height > maxSize) {
        width = (width * maxSize) / height;
        height = maxSize;
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(blob || file),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // fallback: upload original if browser can't decode
    };
    img.src = url;
  });
};

const ScanDevicePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<ScanState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [imei, setImei] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [extractionErrors, setExtractionErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMessage('Invalid scan link');
      return;
    }

    fetch(`/public/scan-tokens/${token}/validate`)
      .then(async (res) => {
        if (res.ok) {
          setState('ready');
        } else {
          const data = await res.json().catch(() => ({}));
          setState('error');
          setErrorMessage(data.error || 'Invalid or expired scan link');
        }
      })
      .catch(() => {
        setState('error');
        setErrorMessage('Could not connect to server. Please check your connection.');
      });
  }, [token]);

  const handleTakePhoto = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setState('processing');

    try {
      console.log(`[ScanDevice] Original: ${file.name}, ${file.type}, ${(file.size / 1024).toFixed(0)}KB`);
      const compressed = await compressImage(file);
      console.log(`[ScanDevice] Compressed: ${(compressed.size / 1024).toFixed(0)}KB (${((1 - compressed.size / file.size) * 100).toFixed(0)}% reduction)`);
      const formData = new FormData();
      formData.append('image', compressed, 'device_sticker.jpg');

      const response = await fetch(`/public/scan-tokens/${token}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        setState('error');
        setErrorMessage(errData.error || 'Failed to process image');
        return;
      }

      const data = await response.json();
      setImei(data.imei || '');
      setSerialNumber(data.serialNumber || '');
      const serverErrors: string[] = data.errors || [];
      const allErrors = [...serverErrors];
      if (data.imei && !isValidImei(data.imei)) {
        allErrors.push(IMEI_RESCAN_MESSAGE);
      }
      setExtractionErrors(allErrors);
      setState('review');
    } catch {
      setState('error');
      setErrorMessage('Failed to upload image. Please check your connection.');
    }

    // Reset file input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!imei || !isValidImei(imei)) {
      setExtractionErrors([IMEI_RESCAN_MESSAGE]);
      return;
    }

    setState('submitting');

    try {
      const response = await fetch(`/public/scan-tokens/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imei, serialNumber: serialNumber || undefined }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        setState('error');
        setErrorMessage(errData.error || 'Failed to submit scan results');
        return;
      }

      setState('success');
    } catch {
      setState('error');
      setErrorMessage('Failed to submit. Please check your connection.');
    }
  };

  const handleRetry = () => {
    setImei('');
    setSerialNumber('');
    setExtractionErrors([]);
    setState('ready');
  };

  return (
    <Theme accentColor="blue" grayColor="slate" radius="medium" appearance="light">
      <Flex
        direction="column"
        align="center"
        justify="center"
        style={{ minHeight: '100vh', padding: '16px', background: '#f8fafc' }}
      >
        <Card size="3" style={{ width: '100%', maxWidth: '440px' }}>
          <Flex direction="column" gap="4" p="4">
            <Text size="5" weight="bold" align="center">
              Device Scanner
            </Text>

            {/* Loading */}
            {state === 'loading' && (
              <Flex direction="column" align="center" gap="3" py="6">
                <div
                  style={{
                    width: 40, height: 40, border: '3px solid #e2e8f0',
                    borderTopColor: '#3b82f6', borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                <Text size="2" color="gray">Validating scan link...</Text>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </Flex>
            )}

            {/* Ready — take photo */}
            {state === 'ready' && (
              <Flex direction="column" align="center" gap="4" py="4">
                <Text size="2" color="gray" align="center">
                  Take a photo of the device sticker to automatically extract the IMEI and serial number.
                </Text>
                <Box
                  style={{
                    padding: '32px', borderRadius: '12px', background: '#f1f5f9',
                    border: '2px dashed #cbd5e1', textAlign: 'center', width: '100%',
                  }}
                >
                  <Text size="6" style={{ display: 'block', marginBottom: '8px' }}>
                    📷
                  </Text>
                  <Text size="2" color="gray">
                    Point your camera at the device sticker with the QR code and serial number
                  </Text>
                </Box>
                <Button size="3" onClick={handleTakePhoto} style={{ width: '100%' }}>
                  Take Photo
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelected}
                  style={{ display: 'none' }}
                />
              </Flex>
            )}

            {/* Processing */}
            {state === 'processing' && (
              <Flex direction="column" align="center" gap="3" py="6">
                <div
                  style={{
                    width: 40, height: 40, border: '3px solid #e2e8f0',
                    borderTopColor: '#3b82f6', borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                <Text size="2" color="gray">Processing image...</Text>
                <Text size="1" color="gray">Scanning for QR code and serial number</Text>
              </Flex>
            )}

            {/* Review results */}
            {state === 'review' && (
              <Flex direction="column" gap="3">
                {extractionErrors.length > 0 && (
                  <Box style={{ padding: '8px 12px', background: '#fef3c7', borderRadius: '6px' }}>
                    {extractionErrors.map((err, i) => (
                      <Text key={i} size="1" style={{ color: '#92400e', display: 'block' }}>
                        {err}
                      </Text>
                    ))}
                  </Box>
                )}

                <Box>
                  <Text size="2" weight="medium" mb="1" style={{ display: 'block' }}>
                    IMEI (Device ID) *
                  </Text>
                  <TextField.Root size="2">
                    <TextField.Input
                      value={imei}
                      onChange={(e) => setImei(e.currentTarget.value)}
                      placeholder="15-digit IMEI number"
                    />
                  </TextField.Root>
                </Box>

                <Box>
                  <Text size="2" weight="medium" mb="1" style={{ display: 'block' }}>
                    Serial Number
                  </Text>
                  <TextField.Root size="2">
                    <TextField.Input
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.currentTarget.value)}
                      placeholder="e.g., 26A001723"
                    />
                  </TextField.Root>
                </Box>

                <Flex gap="2" mt="2">
                  <Button variant="outline" size="2" onClick={handleRetry} style={{ flex: 1 }}>
                    Retake Photo
                  </Button>
                  <Button size="2" onClick={handleSubmit} style={{ flex: 1 }} disabled={!imei}>
                    Confirm
                  </Button>
                </Flex>
              </Flex>
            )}

            {/* Submitting */}
            {state === 'submitting' && (
              <Flex direction="column" align="center" gap="3" py="6">
                <div
                  style={{
                    width: 40, height: 40, border: '3px solid #e2e8f0',
                    borderTopColor: '#3b82f6', borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                <Text size="2" color="gray">Sending device info...</Text>
              </Flex>
            )}

            {/* Success */}
            {state === 'success' && (
              <Flex direction="column" align="center" gap="3" py="6">
                <Box
                  style={{
                    width: 56, height: 56, borderRadius: '50%', background: '#dcfce7',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Text size="6" style={{ color: '#16a34a' }}>✓</Text>
                </Box>
                <Text size="3" weight="medium">Device info sent!</Text>
                <Text size="2" color="gray" align="center">
                  The device details have been sent to the registration form. You can close this page.
                </Text>
              </Flex>
            )}

            {/* Error */}
            {state === 'error' && (
              <Flex direction="column" align="center" gap="3" py="6">
                <Box
                  style={{
                    width: 56, height: 56, borderRadius: '50%', background: '#fee2e2',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Text size="6" style={{ color: '#dc2626' }}>!</Text>
                </Box>
                <Text size="3" weight="medium" color="red">{errorMessage}</Text>
                <Text size="2" color="gray" align="center">
                  Please generate a new scan link from the desktop app.
                </Text>
              </Flex>
            )}
          </Flex>
        </Card>
      </Flex>
    </Theme>
  );
};

export default ScanDevicePage;
